<template>
  <div class="container mt-5 px-4">
    <!-- Header Card -->
    <div class="header-card mb-4">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center gap-3">
          <div class="icon-wrapper">
            <i class="bi bi-diagram-3"></i>
          </div>
          <div>
            <h3 class="mb-1">เพิ่มอุปกรณ์ในกลุ่ม</h3>
            <p class="mb-0 opacity-90">{{ groupname }} <span class="badge text-dark bg-opacity-25 ms-2">#{{ groupId }}</span></p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content Card -->
    <div class="content-card">
      <!-- Toolbar -->
      <div class="toolbar-section">
        <div class="search-box">
          <i class="bi bi-search"></i>
          <input
            v-model.trim="q"
            @keyup.enter="reload()"
            type="search"
            class="search-input"
            placeholder="ค้นหาแผน..."
          />
        </div>

        <div class="toolbar-actions">
          <select v-model.number="size" class="custom-select">
            <option :value="10">10 / หน้า</option>
            <option :value="20">20 / หน้า</option>
            <option :value="50">50 / หน้า</option>
          </select>

          <button class="btn-secondary" :disabled="loading" @click="reload()">
            <i class="bi bi-arrow-clockwise me-2"></i>
            {{ loading ? 'กำลังโหลด...' : 'รีเฟรช' }}
          </button>

          <button class="btn-primary" :disabled="loading" @click="openAddPlanModal">
            <i class="bi bi-plus-circle me-2"></i>
            เพิ่มอุปกรณ์เข้ากลุ่ม
          </button>
        </div>
      </div>

      <!-- Error Alert -->
      <div v-if="error" class="alert alert-danger">
        <i class="bi bi-exclamation-triangle me-2"></i>{{ error }}
      </div>

      <!-- Table -->
      <div class="table-container">
        <table class="modern-table">
          <thead>
            <tr>
              <th style="width: 64px;">#</th>
              <th>ชื่ออุปกรณ์</th>
              <th>รายละเอียดเพิ่มเติม</th>
              <th style="width: 180px;">สร้างเมื่อ</th>
              <th style="width: 120px;">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="5" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-2 mb-0 text-muted">กำลังโหลดข้อมูล...</p>
              </td>
            </tr>

            <tr v-else-if="plans.length === 0">
              <td colspan="5" class="text-center py-5">
                <i class="bi bi-inbox text-muted" style="font-size: 3rem;"></i>
                <p class="mt-2 mb-0 text-muted">ไม่พบข้อมูล</p>
              </td>
            </tr>

            <tr v-else v-for="(plan, idx) in plans" :key="plan.id ?? idx">
              <td><span class="row-number">{{ (page - 1) * size + idx + 1 }}</span></td>
              <td><strong>{{ plan.name ?? '-' }}</strong></td>
              <td class="text-muted">{{ plan.description ?? '-' }}</td>
              <td class="text-muted">{{ formatDate(plan.createdAt) }}</td>
              <td>
                <button 
                  class="btn-action btn-action-danger" 
                  :disabled="loading"
                  @click="removePlan(plan.id)"
                  title="Remove plan"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-section">
        <div class="pagination-info">
          <i class="bi bi-list-ul me-2"></i>
          ทั้งหมด: <strong>{{ meta.total ?? 0 }}</strong> รายการ
        </div>
        <div class="pagination-controls">
          <button class="page-btn" :disabled="page <= 1 || loading" @click="prevPage">
            <i class="bi bi-chevron-left"></i>
            ก่อนหน้า
          </button>
          <span class="page-current">หน้า {{ page }}</span>
          <button class="page-btn" :disabled="disableNext || loading" @click="nextPage">
            ถัดไป
            <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Add Plan Modal -->
  <Transition name="modal-fade">
    <div v-if="showAddPlanModal" class="modal-backdrop" @click.self="closeAddPlanModal">
      <div class="modal-container" role="dialog" aria-modal="true">
        <!-- Modal Header -->
        <div class="modal-header">
          <div class="d-flex align-items-center gap-3">
            <div class="modal-icon">
              <i class="bi bi-plus-circle"></i>
            </div>
            <div>
              <h5 class="mb-0">เพิ่มอุปกรณ์เข้ากลุ่ม</h5>
              <small class="text-muted">เลือกอุปกรณ์ที่ต้องการเพิ่มเข้ากลุ่ม</small>
            </div>
          </div>
          <button class="modal-close" @click="closeAddPlanModal" aria-label="Close">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <!-- Modal Toolbar -->
        <div class="modal-toolbar">
          <div class="search-box flex-grow-1">
            <i class="bi bi-search"></i>
            <input
              v-model.trim="qAllPlans"
              @keyup.enter="reloadAllPlans"
              type="search"
              class="search-input"
              placeholder="ค้นหาแผนทั้งหมด..."
            />
          </div>

          <div class="d-flex align-items-center gap-2">
            <select v-model.number="sizeAllPlans" class="custom-select" style="width: 120px;">
              <option :value="10">10 / หน้า</option>
              <option :value="20">20 / หน้า</option>
              <option :value="50">50 / หน้า</option>
            </select>

            <button class="btn-primary btn-sm" :disabled="loadingAllPlans" @click="reloadAllPlans">
              <i class="bi bi-search me-1"></i>
              ค้นหา
            </button>
          </div>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
          <div v-if="errorAllPlans" class="alert alert-danger mb-3">
            <i class="bi bi-exclamation-triangle me-2"></i>{{ errorAllPlans }}
          </div>

          <div class="modal-table-container">
            <table class="modern-table">
              <thead>
                <tr>
                  <th style="width: 64px;">#</th>
                  <th>ชื่ออุปกรณ์</th>
                  <th>รายละเอียด</th>
                  <th style="width: 120px;">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loadingAllPlans">
                  <td colspan="4" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mt-2 mb-0 text-muted">กำลังโหลดข้อมูล...</p>
                  </td>
                </tr>

                <tr v-else-if="allPlans.length === 0">
                  <td colspan="4" class="text-center py-5">
                    <i class="bi bi-inbox text-muted" style="font-size: 3rem;"></i>
                    <p class="mt-2 mb-0 text-muted">ไม่พบแผน</p>
                  </td>
                </tr>

                <tr v-else v-for="(p, idx) in allPlans" :key="p.id ?? idx">
                  <td><span class="row-number">{{ (pageAllPlans - 1) * sizeAllPlans + idx + 1 }}</span></td>
                  <td><strong>{{ p.name ?? '-' }}</strong></td>
                  <td class="text-muted">{{ p.description ?? '-' }}</td>
                  <td>
                    <button
                      class="btn-action"
                      :class="isPlanAlreadyInGroup(p.id) ? 'btn-action-disabled' : 'btn-action-success'"
                      :disabled="loadingAllPlans || isPlanAlreadyInGroup(p.id)"
                      @click="addPlanById(p.id)"
                    >
                      <i :class="isPlanAlreadyInGroup(p.id) ? 'bi bi-check-circle' : 'bi bi-plus-circle'"></i>
                      {{ isPlanAlreadyInGroup(p.id) ? 'Added' : 'Add' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <div class="pagination-info">
            <i class="bi bi-list-ul me-2"></i>
            ทั้งหมด: <strong>{{ metaAllPlans.total ?? 0 }}</strong> รายการ
          </div>
          <div class="pagination-controls">
            <button class="page-btn btn-sm" :disabled="pageAllPlans <= 1 || loadingAllPlans" @click="prevPageAllPlans">
              <i class="bi bi-chevron-left"></i>
              ก่อนหน้า
            </button>
            <span class="page-current">หน้า {{ pageAllPlans }}</span>
            <button class="page-btn btn-sm" :disabled="disableNextAllPlans || loadingAllPlans" @click="nextPageAllPlans">
              ถัดไป
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>
          <button class="btn-secondary btn-sm ms-3" @click="closeAddPlanModal">
            <i class="bi bi-x-circle me-1"></i>ปิด
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed } from "vue";
import { useRoute, useRouter } from "vue-router";

const route = useRoute();
const router = useRouter();
const rawname = route.params.name || "";
const groupId = route.params.id;
const groupname = ref(decodeURIComponent(rawname));
const API_URL = process.env.VUE_APP_API_URL;
const loading = ref(false);
const error = ref("");
const plans = ref([]);
const meta = ref({ total: 0 });

const q = ref("");
const page = ref(1);
const size = ref(10);

const disableNext = computed(() => {
  const total = meta.value?.total ?? 0;
  return page.value * size.value >= total;
});

function getToken() {
  return localStorage.getItem("accessToken");
}

function authHeaders() {
  const token = getToken();
  return token
    ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

function handle401() {
  localStorage.removeItem("accessToken");
  router.push("/login");
}

function formatDate(dt) {
  if (!dt) return "-";
  try {
    const d = new Date(dt);
    if (isNaN(d)) return String(dt);
    return d.toLocaleString();
  } catch {
    return String(dt);
  }
}

async function apiFetch(url) {
  const res = await fetch(url, { headers: authHeaders() });
  if (res.status === 401) {
    localStorage.removeItem("accessToken");
    router.push("/login");
    throw new Error("Unauthorized");
  }
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(t || `HTTP ${res.status}`);
  }
  return res.json();
}

async function apiPost(url, body) {
  const res = await fetch(url, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) });
  if (res.status === 401) { handle401(); throw new Error("Unauthorized"); }
  if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
  return res.json();
}

async function apiDelete(url) {
  const res = await fetch(url, { method: "DELETE", headers: authHeaders() });
  if (res.status === 401) { handle401(); throw new Error("Unauthorized"); }
  if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
  return res.json().catch(() => ({}));
}

async function load() {
  if (!API_URL) {
    error.value = "ไม่พบค่า VUE_APP_API_URL / VITE_API_URL";
    return;
  }
  loading.value = true;
  error.value = "";
  try {
    const query = new URLSearchParams({
      ...(q.value ? { q: q.value } : {}),
      page: String(page.value),
      size: String(size.value),
    });
    const url = `${API_URL}/api/group/${groupId}/plans?` + query.toString();
    const json = await apiFetch(url);

    plans.value = Array.isArray(json?.data) ? json.data : [];
    meta.value = json?.meta ?? { total: plans.value.length };
  } catch (e) {
    error.value = e?.message ?? "โหลดข้อมูลล้มเหลว";
  } finally {
    loading.value = false;
  }
}

function reload() {
  page.value = 1;
  load();
}

function nextPage() {
  if (!disableNext.value) {
    page.value += 1;
    load();
  }
}

function prevPage() {
  if (page.value > 1) {
    page.value -= 1;
    load();
  }
}

onMounted(load);
watch([() => route.params.id], load);
watch(size, reload);

// Modal Add Plan states
const showAddPlanModal = ref(false);
const allPlans = ref([]);
const metaAllPlans = ref({ total: 0 });
const loadingAllPlans = ref(false);
const errorAllPlans = ref("");

const qAllPlans = ref("");
const pageAllPlans = ref(1);
const sizeAllPlans = ref(10);

const disableNextAllPlans = computed(() => {
  const total = metaAllPlans.value?.total ?? 0;
  return pageAllPlans.value * sizeAllPlans.value >= total;
});

const existingPlanIds = computed(() => new Set(plans.value.map(p => p.id)));

function isPlanAlreadyInGroup(planId) {
  return existingPlanIds.value.has(planId);
}

function openAddPlanModal() {
  showAddPlanModal.value = true;
  pageAllPlans.value = 1;
  loadAllPlans();
}

function closeAddPlanModal() {
  showAddPlanModal.value = false;
}

function onEscPlan(e) {
  if (e.key === "Escape" && showAddPlanModal.value) closeAddPlanModal();
}

onMounted(() => window.addEventListener("keydown", onEscPlan));
onBeforeUnmount(() => window.removeEventListener("keydown", onEscPlan));

async function loadAllPlans() {
  if (!API_URL) { errorAllPlans.value = "ไม่พบค่า VUE_APP_API_URL / VITE_API_URL"; return; }
  loadingAllPlans.value = true; errorAllPlans.value = "";
  try {
    const query = new URLSearchParams({
      ...(qAllPlans.value ? { q: qAllPlans.value } : {}),
      page: String(pageAllPlans.value),
      size: String(sizeAllPlans.value),
    });
    const res = await fetch(`${API_URL}/api/plan?` + query.toString(), { headers: authHeaders() });
    if (res.status === 401) { handle401(); throw new Error("Unauthorized"); }
    if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
    const json = await res.json();
    allPlans.value = Array.isArray(json?.data) ? json.data : [];
    metaAllPlans.value = json?.meta ?? { total: allPlans.value.length };
  } catch (e) {
    errorAllPlans.value = e?.message ?? "โหลดแผนทั้งหมดล้มเหลว";
  } finally {
    loadingAllPlans.value = false;
  }
}

function reloadAllPlans() { pageAllPlans.value = 1; loadAllPlans(); }
function nextPageAllPlans() { if (!disableNextAllPlans.value) { pageAllPlans.value += 1; loadAllPlans(); } }
function prevPageAllPlans() { if (pageAllPlans.value > 1) { pageAllPlans.value -= 1; loadAllPlans(); } }

async function addPlanById(planId) {
  if (!planId) return;
  try {
    loadingAllPlans.value = true;
    await apiPost(`${API_URL}/api/group/${groupId}/plan`, { planId });
    await Promise.all([loadAllPlans(), load()]);
    alert("เพิ่มแผนเข้ากลุ่มสำเร็จ");
  } catch (e) {
    alert("เพิ่มแผนไม่สำเร็จ: " + (e?.message || ""));
  } finally {
    loadingAllPlans.value = false;
  }
}

async function removePlan(planId) {
  if (!window.confirm("ยืนยันลบแผนออกจากกลุ่ม?")) return;
  try {
    loading.value = true;
    await apiDelete(`${API_URL}/api/group/${groupId}/plan/${planId}`);
    await load();
    alert("นำแผนออกจากกลุ่มสำเร็จ");
  } catch (e) {
    alert("ลบไม่สำเร็จ: " + (e?.message || ""));
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
/* Header Card */
.header-card {
  background: linear-gradient(135deg, #66c2ea 0%, #4b7ca2 100%);
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.15);
  color: rgb(255, 255, 255);
}

.icon-wrapper {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  backdrop-filter: blur(10px);
}

.header-card h3 {
  color: rgb(255, 255, 255);
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.header-card p {
  color: rgb(255, 255, 255);
  font-size: 0.95rem;
}

/* Content Card */
.content-card {
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.06);
}

/* Toolbar Section */
.toolbar-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.toolbar-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

/* Search Box */
.search-box {
  position: relative;
  flex: 1;
  min-width: 250px;
  max-width: 400px;
}

.search-box i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #718096;
  font-size: 0.95rem;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  background: #fafafa;
}

.search-input:focus {
  outline: none;
  border-color: #667eea;
  background: white;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Custom Select */
.custom-select {
  padding: 0.75rem 2.5rem 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.9rem;
  background: #fafafa;
  cursor: pointer;
  transition: all 0.3s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%234a5568' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
}

.custom-select:focus {
  outline: none;
  border-color: #667eea;
  background-color: white;
}

/* Buttons */
.btn-primary,
.btn-secondary {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  font-size: 0.95rem;
  white-space: nowrap;
}

.btn-primary {
    background: linear-gradient(135deg, #66c2ea 0%, #4b7ca2 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.btn-secondary {
  background: white;
  color: #718096;
  border: 2px solid #e2e8f0;
}

.btn-secondary:hover:not(:disabled) {
  background: #f7fafc;
  border-color: #cbd5e0;
  color: #2d3748;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.btn-sm {
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
}

/* Alert */
.alert {
  padding: 1rem 1.25rem;
  border-radius: 10px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
}

.alert-danger {
  background: #fee;
  color: #c53030;
  border-left: 4px solid #fc8181;
}

/* Table Container */
.table-container {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1.5rem;
}

/* Modern Table */
.modern-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.modern-table thead {
  background: linear-gradient(to bottom, #f8fafc, #f1f5f9);
  border-bottom: 2px solid #e2e8f0;
}

.modern-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #2d3748;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.modern-table td {
  padding: 1rem;
  border-bottom: 1px solid #f1f5f9;
  color: #4a5568;
  font-size: 0.95rem;
}

.modern-table tbody tr {
  transition: all 0.2s ease;
}

.modern-table tbody tr:hover {
  background: #f8fafc;
}

.modern-table tbody tr:last-child td {
  border-bottom: none;
}

.row-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #e2e8f0;
  color: #4a5568;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
}

/* Action Buttons */
.btn-action {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.btn-action-danger {
  background: #fee;
  color: #c53030;
}

.btn-action-danger:hover:not(:disabled) {
  background: #fc8181;
  color: white;
  transform: translateY(-1px);
}

.btn-action-success {
  background: #c6f6d5;
  color: #22543d;
}

.btn-action-success:hover:not(:disabled) {
  background: #48bb78;
  color: white;
  transform: translateY(-1px);
}

.btn-action-disabled {
  background: #e2e8f0;
  color: #a0aec0;
  cursor: not-allowed;
}

/* Pagination Section */
.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1.5rem;
  border-top: 2px solid #e2e8f0;
}

.pagination-info {
  color: #718096;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
}

.pagination-info strong {
  color: #2d3748;
  margin: 0 0.25rem;
}

.pagination-controls {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.page-btn {
  padding: 0.5rem 1rem;
  border: 2px solid #e2e8f0;
  background: white;
  color: #4a5568;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.page-btn:hover:not(:disabled) {
  background: #f7fafc;
  border-color: #cbd5e0;
  color: #2d3748;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-current {
  padding: 0.5rem 1rem;
    background: linear-gradient(135deg, #66c2ea 0%, #4b7ca2 100%);
  color: white;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
}

/* Modal Styles */
.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(8px);
}

.modal-container {
  width: min(920px, 95vw);
  max-height: 85vh;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  padding: 1.5rem 2rem;
  border-bottom: 2px solid #e2e8f0;
  background: linear-gradient(to bottom, #ffffff, #fafbfc);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-icon {
  width: 48px;
  height: 48px;
    background: linear-gradient(135deg, #66c2ea 0%, #4b7ca2 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.4rem;
}

.modal-header h5 {
  color: #2d3748;
  font-weight: 600;
  font-size: 1.1rem;
}

.modal-close {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #718096;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.modal-close:hover {
  background: #f7fafc;
  color: #2d3748;
}

.modal-toolbar {
  padding: 1.25rem 2rem;
  border-bottom: 1px solid #e2e8f0;
  background: white;
  display: flex;
  gap: 1rem;
  align-items: center;
}

.modal-body {
  flex: 1;
  padding: 1.5rem 2rem;
  overflow: auto;
}

.modal-table-container {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: auto;
  max-height: 50vh;
}

.modal-table-container .modern-table thead th {
  position: sticky;
  top: 0;
  z-index: 10;
  background: linear-gradient(to bottom, #f8fafc, #f1f5f9);
}

.modal-footer {
  padding: 1rem 2rem;
  border-top: 2px solid #e2e8f0;
  background: linear-gradient(to top, #ffffff, #fafbfc);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

/* Modal Transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .modal-container,
.modal-fade-leave-active .modal-container {
  transition: all 0.3s ease;
}

.modal-fade-enter-from .modal-container,
.modal-fade-leave-to .modal-container {
  opacity: 0;
  transform: scale(0.95) translateY(20px);
}

/* Responsive Design */
@media (max-width: 991px) {
  .header-card {
    padding: 1.5rem;
  }

  .header-card h3 {
    font-size: 1.25rem;
  }

  .icon-wrapper {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }

  .content-card {
    padding: 1.5rem;
  }

  .toolbar-section {
    flex-direction: column;
    align-items: stretch;
  }

  .toolbar-actions {
    flex-direction: column;
    width: 100%;
  }

  .toolbar-actions button,
  .toolbar-actions select {
    width: 100%;
  }

  .search-box {
    max-width: 100%;
  }

  .pagination-section {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }

  .pagination-controls {
    width: 100%;
    justify-content: center;
  }

  .modal-container {
    width: 100%;
    max-height: 95vh;
    margin: 10px;
    border-radius: 12px;
  }

  .modal-header,
  .modal-toolbar,
  .modal-body,
  .modal-footer {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .modal-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .modal-toolbar .search-box {
    max-width: 100%;
  }

  .modal-toolbar .d-flex {
    width: 100%;
    justify-content: space-between;
  }

  .modal-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .modal-footer .pagination-controls {
    order: -1;
  }

  .modal-footer button {
    width: 100%;
  }

  .table-container {
    overflow-x: auto;
  }

  .modern-table {
    min-width: 600px;
  }
}

@media (max-width: 576px) {
  .header-card {
    padding: 1rem;
  }

  .header-card h3 {
    font-size: 1.1rem;
  }

  .content-card {
    padding: 1rem;
  }

  .modern-table th,
  .modern-table td {
    padding: 0.75rem;
    font-size: 0.85rem;
  }

  .btn-primary,
  .btn-secondary {
    padding: 0.65rem 1.25rem;
    font-size: 0.9rem;
  }

  .page-btn {
    padding: 0.4rem 0.8rem;
    font-size: 0.85rem;
  }
}

/* Loading Spinner */
.spinner-border {
  width: 2rem;
  height: 2rem;
  border-width: 0.25rem;
}

/* Utility Classes */
.text-muted {
  color: #718096 !important;
}

.text-center {
  text-align: center;
}

.py-5 {
  padding-top: 3rem !important;
  padding-bottom: 3rem !important;
}

.mt-2 {
  margin-top: 0.5rem !important;
}

.mb-0 {
  margin-bottom: 0 !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

.ms-3 {
  margin-left: 1rem !important;
}

.ms-auto {
  margin-left: auto !important;
}

.gap-2 {
  gap: 0.5rem;
}

.gap-3 {
  gap: 1rem;
}

.flex-grow-1 {
  flex-grow: 1;
}

.d-flex {
  display: flex;
}

.align-items-center {
  align-items: center;
}

.justify-content-between {
  justify-content: space-between;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>